import { useCallback, useEffect, useMemo, useState } from 'react';

// Status por quadro: o cliente define a lista (nome, cor e categoria). A
// categoria diz ao sistema como contar o status (concluído, bloqueado...).

export const STATUS_CATEGORIES = [
  { value: 'pending', label: 'A fazer', hint: 'Ainda não começou' },
  { value: 'in_progress', label: 'Em andamento', hint: 'Está sendo trabalhado' },
  { value: 'blocked', label: 'Bloqueado', hint: 'Parado por algum impedimento' },
  { value: 'done', label: 'Concluído', hint: 'Conta como finalizado e tira do atraso' },
];

// Classes completas (não montadas por string) para o Tailwind enxergá-las.
export const STATUS_COLORS = {
  slate: { label: 'Cinza', chip: 'border-slate-200 bg-slate-100 text-slate-600', dot: 'bg-slate-400', solid: 'bg-slate-500' },
  blue: { label: 'Azul', chip: 'border-blue-200 bg-blue-50 text-blue-700', dot: 'bg-blue-500', solid: 'bg-blue-500' },
  green: { label: 'Verde', chip: 'border-emerald-200 bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500', solid: 'bg-green-500' },
  amber: { label: 'Amarelo', chip: 'border-amber-200 bg-amber-50 text-amber-700', dot: 'bg-amber-500', solid: 'bg-yellow-500' },
  red: { label: 'Vermelho', chip: 'border-red-200 bg-red-50 text-red-700', dot: 'bg-red-500', solid: 'bg-red-500' },
  purple: { label: 'Roxo', chip: 'border-purple-200 bg-purple-50 text-purple-700', dot: 'bg-purple-500', solid: 'bg-purple-500' },
  pink: { label: 'Rosa', chip: 'border-pink-200 bg-pink-50 text-pink-700', dot: 'bg-pink-500', solid: 'bg-pink-500' },
  orange: { label: 'Laranja', chip: 'border-orange-200 bg-orange-50 text-orange-700', dot: 'bg-orange-500', solid: 'bg-orange-500' },
  teal: { label: 'Turquesa', chip: 'border-teal-200 bg-teal-50 text-teal-700', dot: 'bg-teal-500', solid: 'bg-teal-500' },
};

export const DEFAULT_STATUSES = [
  { key: 'pendente', label: 'Pendente', color: 'slate', category: 'pending' },
  { key: 'em_progresso', label: 'Em progresso', color: 'blue', category: 'in_progress' },
  { key: 'parado', label: 'Parado', color: 'red', category: 'blocked' },
  { key: 'feito', label: 'Feito', color: 'green', category: 'done' },
];

// Apelidos antigos que ainda existem em tarefas já criadas. Espelha o
// _normalize_project_status_key do backend.
const LEGACY_ALIASES = {
  done: 'feito', completed: 'feito', concluido: 'feito', 'concluído': 'feito',
  em_andamento: 'em_progresso', working_on_it: 'em_progresso', in_progress: 'em_progresso', doing: 'em_progresso',
  blocked: 'parado', stuck: 'parado', bloqueado: 'parado',
  not_started: 'pendente', todo: 'pendente', to_do: 'pendente', to_doo: 'pendente',
};

export function canonicalStatusKey(raw) {
  const normalized = String(raw || '').trim().toLowerCase().replace(/-/g, '_').replace(/\s+/g, '_');
  return LEGACY_ALIASES[normalized] || normalized;
}

export function slugifyStatus(label) {
  return String(label || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

// Resolve um valor bruto de status (inclusive apelido antigo) na definição do quadro.
export function resolveStatus(raw, statuses) {
  if (!raw) return { key: '', label: 'Sem status', color: 'amber', category: null, chip: 'border-amber-200 bg-amber-50 text-amber-700', dot: 'bg-amber-500', solid: 'bg-gray-400' };
  const wanted = canonicalStatusKey(raw);
  const found = (statuses || DEFAULT_STATUSES).find(status => canonicalStatusKey(status.key) === wanted);
  const base = found || { key: raw, label: String(raw).replace(/_/g, ' '), color: 'slate', category: null };
  return { ...base, ...(STATUS_COLORS[base.color] || STATUS_COLORS.slate), label: base.label };
}

const listeners = new Set();
export function notifyStatusesChanged(boardId) {
  listeners.forEach(listener => listener(boardId));
}

export function useBoardStatuses(api, boardId) {
  const [statuses, setStatuses] = useState(DEFAULT_STATUSES);
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(async () => {
    if (!api || !boardId) return;
    try {
      const response = await api.get(`/projects/boards/${boardId}/statuses`);
      if (Array.isArray(response.data?.statuses) && response.data.statuses.length) setStatuses(response.data.statuses);
    } catch {
      // mantém os padrões se a leitura falhar
    } finally {
      setLoaded(true);
    }
  }, [api, boardId]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    const listener = changedBoardId => { if (!changedBoardId || changedBoardId === boardId) reload(); };
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, [boardId, reload]);

  const options = useMemo(() => statuses.map(status => status.key), [statuses]);
  return { statuses, options, loaded, reload };
}
